import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shared/PageHeader';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as passengerSlice from '../../../data/passenger.slice';
import { useDate } from '../../../hooks/display/useDate';
import { useAddress } from '../../../hooks/display/useAddress';
import { usePassengerPhone } from '../../../hooks/display/usePassengerPhone';
import PassengerPreferencesDialog from '../../home/passengers/PassengerPreferencesDialog';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { PassengerPreferenceBC } from '../../../data/models/passenger.model';
import { useFeatureMemberPortalMemberPreferences } from '../../../hooks/features/useFeatureMemberPreferences';

const PREFIX = 'SettingsPage';

const classes = {
  container: `${PREFIX}-container`,
  body: `${PREFIX}-body`,
  header: `${PREFIX}-header`,
  row: `${PREFIX}-row`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  [`&.${classes.body}`]: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '750px',
    width: '100%',
    gap: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  [`&.${classes.row}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const SettingsPage = () => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const passengerPreferences = useAppSelector(
    passengerSlice.selectCurrentPassengerPreferences,
  );

  useEffect(() => {
    if (passenger) {
      dispatch(
        passengerSlice.fetchPassengerPreferencesAsync(passenger.passengerUUID!),
      );
    }
  }, [passenger]);
  const isMemberPortalMemberPreferencesEnabled =
    useFeatureMemberPortalMemberPreferences();

  const landlinePhone = usePassengerPhone(
    passenger?.passengerPhones,
    'LANDLINE',
  );
  const naPhone = usePassengerPhone(passenger?.passengerPhones, 'NA');

  const [preferenceDialogOpen, setPreferenceDialogOpen] = React.useState(false);

  const editPreference = () => {
    if (isMemberPortalMemberPreferencesEnabled) {
      setPreferenceDialogOpen(true);
    }
  };

  const getPreferenceValue = (attrBC: string) => {
    const pref = passengerPreferences.find(
      (pp) => pp.passengerProfileAttributeTypeBC === attrBC,
    );
    return pref?.attributeValue || '';
  };

  return (
    <StyledBox className={classes.container}>
      <PageHeader title={t('member.settings')} />
      <StyledBox className={classes.body}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h3" component="h2">
                {t('member.profile')}
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body1" className="bold">
                  {[
                    passenger?.firstName,
                    passenger?.middleName,
                    passenger?.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </Typography>
                <Box>
                  <Typography>{useDate(passenger?.dateOfBirth)}</Typography>
                  <Typography>{passenger?.passengerEmailAddress}</Typography>
                  <Typography>{landlinePhone || naPhone}</Typography>
                  <Typography>
                    {usePassengerPhone(passenger?.passengerPhones, 'MOBILE')}
                  </Typography>
                  <Typography>
                    {passenger?.passengerAddresses && (
                      <>
                        {useAddress(
                          passenger?.passengerAddresses?.find(
                            (addr) => addr.passengerAddressUseBC === 'MANUAL',
                          ),
                        )}
                      </>
                    )}
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body1" className="bold">
                  {t('member.memberId')}
                </Typography>
                <Typography variant="body1">
                  {passenger?.clientPassengerID}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {isMemberPortalMemberPreferencesEnabled && (
          <>
            <Card>
              <CardContent>
                <Stack spacing={3}>
                  <Box className={classes.header}>
                    <Typography variant="h3" component="h2">
                      {t('passenger.preferences')}
                    </Typography>
                    <Tooltip title={t('passenger.editPreferences')}>
                      <span>
                        <IconButton
                          data-testid="delete-address"
                          onClick={(evt) => editPreference()}
                          color="primary">
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Stack spacing={1}>
                    <Typography variant="body1" className="bold">
                      {t('passenger.communication')}
                    </Typography>
                    <Typography variant="body1">
                      {getPreferenceValue(PassengerPreferenceBC.communication)}
                    </Typography>
                    <Typography variant="body1" className="bold">
                      {t('passenger.language')}
                    </Typography>
                    <Typography variant="body1">
                      {getPreferenceValue(PassengerPreferenceBC.language)}
                    </Typography>
                    <Typography variant="body1" className="bold">
                      {t('passenger.provider')}
                    </Typography>
                    <Typography variant="body1">
                      {getPreferenceValue(PassengerPreferenceBC.provider)}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            <PassengerPreferencesDialog
              passengerUUID={passenger?.passengerUUID!}
              passengerPreferences={passengerPreferences}
              open={preferenceDialogOpen}
              close={() => setPreferenceDialogOpen(false)}
              isMemberPortal={true}
            />
          </>
        )}
      </StyledBox>
    </StyledBox>
  );
};

export default SettingsPage;
